import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Button from '@mui/material/Button';

import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getDisplayName } from '../../../utils/getDisplayName';
import { isCurrentUser, rankLabelsByTotal, ranksByTotal } from '../../../utils/leaderboardUtils';
import YouChip from './YouChip';
import WeeklyBreakdownModal from './WeeklyBreakdownModal';
import { RankBadge } from './SoftMarks';
import { soft, softContainerSx, softHeadCellSx, softBodyCellSx, meRowFirstCellShadow } from './softStyles';

// Plain (single-class) styles so per-cell sx overrides still win.
const StyledTableCell = styled(TableCell)(softBodyCellSx);
const HeadCell = styled(TableCell)(softHeadCellSx);

const StyledTableRow = styled(TableRow)(() => ({
  '& td': {
    backgroundColor: '#FFFFFF',
    transition: 'background-color 150ms ease',
  },
  // The signed-in player's row.
  '&.me td': {
    backgroundColor: soft.meBg,
  },
  '&.me td:first-of-type': {
    boxShadow: meRowFirstCellShadow,
  },
  // Clickable rows (open the weekly breakdown).
  '&.MuiTableRow-hover:hover td': {
    backgroundColor: soft.hoverBg,
  },
  '&.me.MuiTableRow-hover:hover td': {
    backgroundColor: soft.meHoverBg,
  },
  '&:focus-visible': {
    outline: `2px solid ${soft.accent}`,
    outlineOffset: '-2px',
  },
}));

// Mobile expandable row component
const MobileRow = ({ row, rank, tied, displayName, isMe, onShowBreakdown }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <StyledTableRow
        className={isMe ? 'me' : undefined}
        onClick={() => setOpen(!open)}
        sx={{ cursor: 'pointer' }}
      >
        <StyledTableCell align="left" sx={{ width: '40px', padding: '10px 8px 10px 12px' }}>
          <RankBadge rank={rank} tied={tied} />
        </StyledTableCell>
        <StyledTableCell align="left" sx={{ padding: '10px 8px', fontWeight: isMe ? 500 : 400 }}>
          {getDisplayName(displayName, row.name, { truncate: 18 })}
          {isMe && <YouChip />}
        </StyledTableCell>
        <StyledTableCell align="right" sx={{ fontWeight: 500, padding: '10px 8px' }}>
          {row.totalPoints}
        </StyledTableCell>
        <StyledTableCell align="right" sx={{ padding: '4px 8px 4px 0', width: '30px' }}>
          <IconButton size="small" aria-label={open ? 'Hide details' : 'Show details'} sx={{ color: soft.faint }}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </StyledTableCell>
      </StyledTableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ padding: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <div style={{
              padding: '10px 16px 4px',
              display: 'flex',
              justifyContent: 'space-around',
              fontSize: '12px',
              color: soft.muted,
              backgroundColor: soft.headBg,
            }}>
              <span>Score pts <strong style={{ color: soft.text, fontWeight: 500 }}>{row.pickScorePoints}</strong></span>
              <span>Winner pts <strong style={{ color: soft.text, fontWeight: 500 }}>{row.pickWinnerPoints}</strong></span>
            </div>
            <div style={{ textAlign: 'center', backgroundColor: soft.headBg, paddingBottom: '8px' }}>
              <Button size="small" onClick={() => onShowBreakdown(row)} sx={{ textTransform: 'none', borderRadius: '999px' }}>
                Weekly breakdown
              </Button>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const LeaderboardTable = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getLeaderboard, userDisplayNames } = useData();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('totalPoints');
  // The player stays set after closing so the dialog keeps its header while it fades out.
  const [breakdownPlayer, setBreakdownPlayer] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  useEffect(() => {
    fetchLeaderboard();
  }, [props.season, getLeaderboard]);

  const fetchLeaderboard = () => {
    setIsLoading(true);
    getLeaderboard(props.season).then((response) => {
      // Doc id is the player's email (weeklyPoints lives under it).
      const newLeaderboard = response.map(doc => ({ ...doc.data(), email: doc.id || doc.data().name }));
      setLeaderboard(newLeaderboard);
      setIsLoading(false);
    }).catch((error) => {
      console.error('Error fetching leaderboard:', error);
      setIsLoading(false);
    });
  }

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRows = leaderboard.slice().sort((a, b) => {
    if (orderBy) {
      const valueA = orderBy === 'name' ? a[orderBy] : parseFloat(a[orderBy]);
      const valueB = orderBy === 'name' ? b[orderBy] : parseFloat(b[orderBy]);

      if (!isNaN(valueA) && !isNaN(valueB)) {
        return order === 'asc' ? valueA - valueB : valueB - valueA;
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return order === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }

      return 0;
    }
    return 0;
  });

  // Rank follows total points whatever column the table is sorted by.
  const ranks = ranksByTotal(leaderboard);
  const rankLabels = rankLabelsByTotal(leaderboard);
  const showBreakdown = (row) => {
    setBreakdownPlayer({
      email: row.email,
      displayName: getDisplayName(userDisplayNames.get(row.name), row.name),
      totalPoints: row.totalPoints,
      rankLabel: rankLabels[row.email],
    });
    setBreakdownOpen(true);
  };
  const breakdownModal = (
    <WeeklyBreakdownModal
      open={breakdownOpen}
      onClose={() => setBreakdownOpen(false)}
      season={props.season}
      player={breakdownPlayer}
      currentWeek={props.currentWeek}
      onSelectWeek={props.onSelectWeek}
    />
  );

  const sortLabel = (property, label) => (
    <TableSortLabel
      active={orderBy === property}
      direction={orderBy === property ? order : 'asc'}
      onClick={() => handleRequestSort(property)}
    >
      {label}
    </TableSortLabel>
  );

  // Mobile: compact standings with expandable rows
  if (isMobile) {
    return (
      <TableContainer sx={{ ...softContainerSx, overflowX: 'auto' }}>
        <Table size="small" aria-label="standings" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              <HeadCell sx={{ padding: '10px 8px 10px 12px', width: '40px' }}>#</HeadCell>
              <HeadCell sx={{ padding: '10px 8px' }}>Player</HeadCell>
              <HeadCell align="right" sx={{ padding: '10px 8px' }}>
                {sortLabel('totalPoints', 'Points')}
              </HeadCell>
              <HeadCell sx={{ padding: '4px', width: '30px' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => (
              <MobileRow
                key={row.name}
                row={row}
                rank={ranks[row.email].rank}
                tied={ranks[row.email].tied}
                displayName={userDisplayNames.get(row.name)}
                isMe={isCurrentUser(row.name, currentUser)}
                onShowBreakdown={showBreakdown}
              />
            ))}
          </TableBody>
        </Table>
        {breakdownModal}
      </TableContainer>
    );
  }

  // Desktop: full table; a row opens that player's weekly breakdown
  return (
    <TableContainer sx={{ ...softContainerSx, overflowX: 'auto' }}>
      <Table sx={{ minWidth: 700, borderCollapse: 'separate', borderSpacing: 0 }} aria-label="standings">
        <TableHead>
          <TableRow>
            <HeadCell sx={{ width: '48px' }}>#</HeadCell>
            <HeadCell align="left">Player</HeadCell>
            <HeadCell align="right">{sortLabel('pickScorePoints', 'Score pts')}</HeadCell>
            <HeadCell align="right">{sortLabel('pickWinnerPoints', 'Winner pts')}</HeadCell>
            <HeadCell align="right">{sortLabel('totalPoints', 'Total')}</HeadCell>
            <HeadCell sx={{ width: '40px' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row) => {
            const isMe = isCurrentUser(row.name, currentUser);
            return (
              <StyledTableRow
                key={row.name}
                className={isMe ? 'me' : undefined}
                hover
                onClick={() => showBreakdown(row)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showBreakdown(row); } }}
                tabIndex={0}
                sx={{ cursor: 'pointer' }}
                title="Show weekly breakdown"
              >
                <StyledTableCell><RankBadge rank={ranks[row.email].rank} tied={ranks[row.email].tied} /></StyledTableCell>
                <StyledTableCell align="left" sx={{ fontWeight: isMe ? 500 : 400 }}>
                  {getDisplayName(userDisplayNames.get(row.name), row.name)}
                  {isMe && <YouChip />}
                </StyledTableCell>
                <StyledTableCell align="right">{row.pickScorePoints}</StyledTableCell>
                <StyledTableCell align="right">{row.pickWinnerPoints}</StyledTableCell>
                <StyledTableCell align="right" sx={{ fontWeight: 500 }}>{row.totalPoints}</StyledTableCell>
                <StyledTableCell align="right" sx={{ color: soft.faint, padding: '0 10px' }}>
                  <ChevronRightIcon fontSize="small" aria-label="Weekly breakdown" sx={{ color: soft.faint, verticalAlign: 'middle' }} />
                </StyledTableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
      {breakdownModal}
    </TableContainer>
  );
};

export default React.memo(LeaderboardTable);
